<script>
import { defineAsyncComponent } from 'vue';
import addToCart from '../../services/gift-card-create';
import analytics from '../../common/analytics';
import CONSTANTS from '../../utils/constants';
import getCustomEvent from '../../common/getCustomEvent';
import getScreenWidth from '../../utils/getScreenWidth';
import GiftcardDeliveryMethodComponent from '../giftcard-delivery-method/GiftcardDeliveryMethodComponent.vue';
import GiftcardListComponent from '../giftcard-list/GiftcardListComponent.vue';
import isMobile from '../../utils/isMobile';

export default {
  name: 'GiftcardFormComponent',
  components: {
    GiftcardDeliveryMethodComponent,
    GiftcardFormInput: defineAsyncComponent(() => import('../giftcard-form-inputs/GiftcardFormInputsComponent.vue')),
    GiftcardListComponent,
  },
  props: {
    cards: {
      type: Object,
      required: true,
    },
    cmsContent: {
      type: Object,
      required: true,
    },
    siteId: {
      type: String,
      required: true,
    },
    pageDescription: {
      type: String,
      required: true,
    },
    productStyles: {
      type: String,
      required: true,
    },
    tabsData: {
      type: Array,
      required: true,
    },
    preFillValue: {
      type: String,
      default: '0.00',
    },
  },
  emits: ['openModal'],
  data() {
    return {
      deliveryMethod: 'EMAIL',
      imgSelected: {},
      screenWidth: '',
      isMobile: false,
      analytics,
      tabDeliveryOffset: 0,
      isFormOpen: false,
      errors: [],
      openAlert: false,
    };
  },
  watch: {
    deliveryMethod(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.deliveryDateInput = newValue === 'EMAIL'
        ? `${this.monthInput}/${this.dayInput}/${this.yearInput}`
        : '';
      this.sendAnalyticClickEvent(
        `select ${newValue === 'EMAIL' ? 'email' : 'mail'}`
      );
      this.isFormOpen = false;
    },
  },
  mounted() {
    window.addEventListener('resize', this.detectBreakPoint);
    Object.freeze(CONSTANTS);
    this.deliveryDateInput = `${this.monthInput}/${this.dayInput}/${this.yearInput}`;
    this.tabDeliveryOffset = this.$refs.deliveryTabs.$el.offsetTop;
    this.detectBreakPoint();
    this.sendAnalyticClickEvent('select email');
  },
  unmounted() {
    window.removeEventListener('resize', this.detectBreakPoint);
  },
  methods: {
    detectBreakPoint() {
      this.screenWidth = getScreenWidth();
      this.isMobile = isMobile(this.screenWidth);
    },
    onOpenModal(giftCardInformation) {
      this.$emit('openModal', giftCardInformation);
    },
    async onPurchaseAnalytic(data = {}) {
      const errorMessage = data?.analyticsErrorMsg?.toString();
      const todayDate = new Date().setHours(0, 0, 0, 0);
      const deliveryDate = new Date(data?.deliveryDate).setHours(0, 0, 0, 0);
      const analyticBody = {
        linkName: `${this.$props.siteId}:${this.$props.pageDescription}_add to cart`,
        productStyles: this.$props.productStyles,
        addToCartLocation: 'add to cart',
        checkoutMediaCode: data?.giftCardSelected?.mediaCode,
        giftCardEad: todayDate === deliveryDate ? 0 : 1,
        ...(data?.error && { errorMessage }),
      };

      await this.analytics.click(analyticBody);
    },
    async onPurchaseForm(giftCardItem) {
      const analyticData = {
        giftCardSelected: { mediaCode: giftCardItem?.mediaCode },
        deliveryDate: giftCardItem?.deliveryDate,
      };
      try {
        const response = await addToCart.addToCart(giftCardItem);
        if (response?.ok && response?.status === 201) {
          await this.onPurchaseAnalytic(analyticData);
          await this.cartCounterUpdate();
          // TODO: Change to kebab case
          await this.navigation('/ShoppingCart');
        } else if (response?.status === 400) {
          response.json().then(({ errors }) => {
            this.errors = errors || this.errors;
            this.openAlert = !!errors.length > 0;
          });
        } else {
          this.openAlert = true;
          throw Error(CONSTANTS.MSG_ERROR_SERVICE_ADD_CART);
        }
      } catch (error) {
        // something is wrong with the cart service
        this.openAlert = true;
        throw error;
      }
    },
    async navigation(path) {
      window.location = path;
    },
    async cartCounterUpdate() {
      const CustomEvent = getCustomEvent();
      document.dispatchEvent(new CustomEvent('rei.gnav.cartCountChange'));
    },
    sendAnalyticClickEvent(eventName) {
      this.analytics.click({
        linkName: `${this.$props.siteId}:${this.$props.pageDescription}_${eventName}`,
      });
    },
    onChangeDelivery(tabMethod) {
      this.deliveryMethod = tabMethod === 'e-gift-cards-panel' ? 'EMAIL' : 'MAIL';
    },
    onSelectGiftCard(selectedGiftCard) {
      this.imgSelected = selectedGiftCard;
      this.isFormOpen = selectedGiftCard.selected;

      if (selectedGiftCard.selected) {
        this.sendAnalyticClickEvent(
          `select_card_${selectedGiftCard.description}`
        );
      }
    },
    onCloseForm(closeModalValue) {
      this.isFormOpen = closeModalValue;
      if (!this.isMobile) window.scrollTo({ top: 480 });
    },
    onUpdateIsOpenAlert(isAlertOpen) {
      this.openAlert = isAlertOpen;
    },
  },
};
</script>

<template>
  <section class="container-static">
    <giftcard-delivery-method-component
      ref="deliveryTabs"
      v-bind="{
        tabsData,
        siteId,
        pageDescription,
      }"
      @changeDelivery="onChangeDelivery"
    />
    <div
      class="giftcard-form__container"
      :style="{ borderTop: 'none', paddingTop: 0 }"
    >
      <giftcard-list-component
        :class="`giftcard-list__container ${
          !isMobile && isFormOpen ? 'form-view' : ''
        }`"
        v-bind="{
          deliveryMethod,
          cards,
          isMobile,
          tabDeliveryPosition: tabDeliveryOffset,
          isFormOpen,
          preFillValue,
        }"
        @onSelectGiftCard="onSelectGiftCard"
      />
      <giftcard-form-input
        v-if="isFormOpen"
        class="giftcard-form-input__container"
        v-bind="{
          showForm: isFormOpen,
          imgSelected,
          cmsContent,
          deliveryMethod,
          errors,
          openAlert,
          siteId,
          pageDescription,
          productStyles,
          preFillValue
        }"
        @onPurchaseAnalytic="onPurchaseAnalytic"
        @onCloseForm="onCloseForm"
        @onPreview="onOpenModal"
        @onPurchaseForm="onPurchaseForm"
        @onUpdateIsOpenAlert="onUpdateIsOpenAlert"
      />
    </div>
  </section>
</template>
